import React from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Clock,
  Sparkles,
  ArrowUpRight,
  Construction,
  Cog,
  Star,
} from "lucide-react";
import { carwashes } from "../data/carwashes";

const Landing = () => {
  return (
    <main className="relative z-10 pt-24 pb-12 min-h-screen" data-testid="landing-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20">
        <div className="reveal">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-400/10 border border-cyan-400/20 text-cyan-300 text-xs font-semibold uppercase tracking-[0.18em] mb-8 font-body">
            <Sparkles className="w-3.5 h-3.5" />
            Self-service · Friuli Venezia Giulia
          </div>
          <h1
            data-testid="hero-title"
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-5xl"
          >
            <span className="text-white">Scegli il tuo</span>
            <br />
            <span className="shimmer-text">autolavaggio.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed font-body">
            Tre sedi, una sola promessa: lavaggio impeccabile, tecnologia di ultima
            generazione e brillantezza che dura. Lavoriamo 24 ore al giorno,
            così la tua auto è sempre pronta a brillare.
          </p>
        </div>

        {/* Cards */}
        <div
          data-testid="locations-grid"
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mt-16"
        >
          <LocationCard data={carwashes.villesse} delay="0.1s" />
          <LocationCard data={carwashes.tavagnacco} delay="0.25s" />
          <WipCard delay="0.4s" />
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-20">
          {[
            { label: "Sedi attive", value: "2" },
            { label: "Ore di apertura / giorno", value: "24" },
            { label: "Valutazione Villesse", value: "4.6★" },
            { label: "Valutazione Tavagnacco", value: "4.5★" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`stat-${i}`}
              className="glass rounded-2xl p-6 text-center reveal"
              style={{ animationDelay: `${0.5 + i * 0.08}s` }}
            >
              <div className="font-display font-black text-3xl sm:text-4xl text-cyan-400">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-[0.15em] text-slate-400 mt-2 font-body">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

const LocationCard = ({ data, delay }) => {
  return (
    <Link
      to={`/${data.slug}`}
      data-testid={`card-${data.slug}`}
      className="group card-lift relative overflow-hidden rounded-3xl border border-white/10 glass reveal"
      style={{ animationDelay: delay }}
    >
      <div className="absolute inset-0">
        <img
          src={data.heroImage}
          alt={data.name}
          className="w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/30" />
      </div>

      <div className="relative p-7 lg:p-8 flex flex-col min-h-[440px]">
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-300 font-bold font-body">
            Sede attiva
          </span>
          {data.rating ? (
            <span className="flex items-center gap-1 text-xs text-slate-300">
              <Star className="w-3.5 h-3.5 fill-amber-400 stroke-amber-400" />
              <span className="font-semibold">{data.rating}</span>
              {data.reviewsCount && (
                <span className="text-slate-500">({data.reviewsCount})</span>
              )}
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-body font-bold">
              24/7
            </span>
          )}
        </div>

        <h3 className="font-display font-black text-3xl lg:text-4xl text-white mt-4 leading-tight tracking-tight">
          {data.shortAddress.split(" ")[0]}
        </h3>
        <p className="text-slate-300 font-body text-sm mt-1">{data.brand}</p>

        <div className="flex-1" />

        <div className="space-y-2.5 mt-8 text-sm text-slate-300 font-body">
          <div className="flex items-start gap-2.5">
            <MapPin className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
            <span>{data.address}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Clock className="w-4 h-4 text-cyan-400 flex-shrink-0" />
            <span>{data.hours}</span>
          </div>
        </div>

        <div className="mt-7 flex items-center justify-between">
          <span className="font-display font-bold text-base text-white group-hover:text-cyan-300 transition-colors">
            Scopri la sede
          </span>
          <span className="w-10 h-10 rounded-full bg-cyan-400/10 border border-cyan-400/30 grid place-items-center group-hover:bg-cyan-400 group-hover:text-slate-950 group-hover:scale-110 transition-all">
            <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
          </span>
        </div>
      </div>

      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${data.accent} opacity-70`}
      />
    </Link>
  );
};

const WipCard = ({ delay }) => {
  return (
    <Link
      to="/work-in-progress"
      data-testid="card-wip"
      className="group card-lift relative overflow-hidden rounded-3xl border border-amber-400/20 reveal"
      style={{
        animationDelay: delay,
        background:
          "radial-gradient(circle at 20% 0%, rgba(250, 204, 21, 0.1), transparent 50%), rgba(8, 16, 28, 0.6)",
        backdropFilter: "blur(20px)",
      }}
    >
      {/* Construction tape stripe */}
      <div className="absolute top-0 left-0 right-0 h-2 construction-stripes opacity-90" />
      <div className="absolute bottom-0 left-0 right-0 h-2 construction-stripes opacity-90" />

      <div className="relative p-7 lg:p-8 flex flex-col min-h-[440px]">
        <span className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold font-body">
          In arrivo
        </span>

        <div className="flex-1 grid place-items-center my-6">
          <div className="relative">
            <span className="absolute inset-0 rounded-full bg-amber-400/30 pulse-ring" />
            <span
              className="absolute inset-0 rounded-full bg-amber-400/20 pulse-ring"
              style={{ animationDelay: "1.2s" }}
            />
            <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-amber-400 to-yellow-600 grid place-items-center shadow-[0_0_40px_rgba(250,204,21,0.4)]">
              <Cog className="w-12 h-12 text-slate-950 rotate-gear" strokeWidth={2} />
              <Cog
                className="w-7 h-7 text-slate-950 absolute -bottom-1 -right-1 rotate-gear-reverse"
                strokeWidth={2}
              />
            </div>
          </div>
        </div>

        <h3 className="font-display font-black text-3xl text-white tracking-tight text-center">
          Work in Progress
        </h3>
        <p className="text-amber-200/80 text-sm mt-2 font-body text-center px-2 leading-relaxed">
          Stiamo lavorando alla terza sede DMB S.r.l. Qui ci saranno due
          lavaggi automatici di ultima generazione, 4 piste in modalità
          self-service, 8 postazioni di aspirazione con aria compressa,
          distributore per l&apos;acquisto di profumatori e vari pelli.
          Ancora poco e potrai venire a provarla.
        </p>

        <div className="mt-7 flex items-center justify-center gap-2 text-amber-300 font-display font-bold text-sm">
          <Construction className="w-4 h-4" />
          <span className="uppercase tracking-[0.18em] text-xs">Scopri di più</span>
        </div>
      </div>
    </Link>
  );
};

export default Landing;
